import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { mdiAutoFix } from '@mdi/js'

import { apiClient } from '../../api/client'
import type { AiProvidersResponse } from '../../api/schema'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { notify } from '../../lib/notifications'
import { PageHeader } from '../shared/PageHeader'
import { EmbeddingSwitchSection } from './EmbeddingSwitchSection'
import { OcrProviderSection } from './OcrProviderSection'
import { AgentSettingsSection } from './AgentSettingsSection'
import { AgentUsageSection } from './AgentUsageSection'
import { FamilyTable, type FamilyChange } from './FamilyTable'

const FAMILY_CODES: Record<FamilyChange['family'], { provider: string; model: string }> = {
    extraction: { provider: 'ai.extraction.provider', model: 'ai.extraction.model' },
    vision: { provider: 'ai.vision.provider', model: 'ai.vision.model' },
    transcription: { provider: 'ai.transcription.provider', model: 'ai.transcription.model' },
}

type SettingsWriter = {
    POST: (
        path: string,
        opts: { params: { path: { orgCode: string; code: string } }; body: { value: unknown } }
    ) => Promise<{ data?: unknown; error?: unknown }>
    DELETE: (
        path: string,
        opts: { params: { path: { orgCode: string; code: string } } }
    ) => Promise<{ data?: unknown; error?: unknown }>
}

/**
 * The AI models screen (spec 2026-10-10-01): which declared provider and model
 * run this organization's data extraction. Everyone in the organization can
 * read the choice; only platform admins can change it, because where a
 * customer's documents are sent is a contractual matter. Changing it does not
 * recompute existing extractions.
 */
export function AIModelsSettingsPage({ org }: { org: string }) {
    const { t } = useTranslation('settings')
    const queryClient = useQueryClient()

    useBreadcrumbs(
        useMemo(
            () => [
                { title: t('common:nav.home'), path: `/${org}` },
                { title: t('page.title'), path: `/${org}/settings` },
                { title: t('ai_models.breadcrumb') },
            ],
            [org, t]
        )
    )

    const queryKey = ['ai-providers', org]
    const { data, isLoading } = useQuery({
        queryKey,
        queryFn: async (): Promise<AiProvidersResponse> => {
            const { data: body, error } = await apiClient.GET(
                '/api/v1/organizations/{orgCode}/settings/ai-providers',
                { params: { path: { orgCode: org } } }
            )
            if (error || !body) throw new Error('Failed to load the AI providers')
            return body.data
        },
    })

    const writer = apiClient as unknown as SettingsWriter
    const path = '/api/v1/organizations/{orgCode}/settings/{code}'

    const mutation = useMutation({
        mutationFn: async (next: FamilyChange) => {
            const codes = FAMILY_CODES[next.family]
            const params = (code: string) => ({ params: { path: { orgCode: org, code } } })
            // Provider first: the server checks the model against the provider
            // the organization has when the model is written. An empty
            // provider clears the family's own choice (it follows Text, or the
            // stack setting for transcription).
            const providerRes = next.provider
                ? await writer.POST(path, { ...params(codes.provider), body: { value: next.provider } })
                : await writer.DELETE(path, params(codes.provider))
            if (providerRes.error) throw providerRes.error
            const modelRes = next.model
                ? await writer.POST(path, { ...params(codes.model), body: { value: next.model } })
                : await writer.DELETE(path, params(codes.model))
            if (modelRes.error) throw modelRes.error
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey })
            notify({ key: 'ai_models_saved', variant: 'default', titleKey: 'settings:ai_models.saved' })
        },
        onError: () => {
            notify({
                key: 'ai_models_error',
                variant: 'destructive',
                titleKey: 'settings:ai_models.error_title',
                descriptionKey: 'settings:ai_models.error_description',
                timeoutMs: 0,
            })
        },
    })

    if (isLoading || !data) {
        return (
            <div className="container mx-auto py-8 max-w-3xl text-muted-foreground" data-testid="ai-models-loading">
                {t('ai_models.loading')}
            </div>
        )
    }

    const { providers: declared, ocr, agent, can_write: canWrite } = data

    return (
        <div className="container mx-auto py-8 max-w-4xl" data-testid="ai-models-page">
            <PageHeader
                className="mb-8"
                icon={mdiAutoFix}
                title={t('ai_models.title')}
                description={t('ai_models.subtitle')}
            />

            <FamilyTable data={data} canWrite={canWrite} pending={mutation.isPending} onChange={(c) => mutation.mutate(c)} />

            <div className="mt-6">
                <OcrProviderSection
                    org={org}
                    provider={ocr.provider}
                    options={ocr.options}
                    canWrite={canWrite}
                    queryKey={queryKey}
                />
            </div>

            <div className="mt-6">
                <EmbeddingSwitchSection org={org} />
            </div>

            <div className="mt-6">
                <AgentSettingsSection org={org} agent={agent} providers={declared} canWrite={canWrite} queryKey={queryKey} />
            </div>

            <div className="mt-6">
                <AgentUsageSection org={org} />
            </div>
        </div>
    )
}
